import { Injectable } from '@angular/core';
import { environment } from '@y42/environment';
import { ErrorHandlerService } from '@y42/error-handling';
import { DatasourceTypeEnum } from '@y42/json-schema';
import { Observable } from 'rxjs';
import { DatasourceSettings } from '../datasource/datasource.model';
import { DatasourceTypesContentfulService } from './datasource-type-contentful-state.service';
import { DatasourceTypeModel } from './datasource-type.model';

@Injectable({
  providedIn: 'root',
})
export class DatasourceTypeQuery {
  constructor(
    private readonly datasourceCTypeService: DatasourceTypesContentfulService,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {}

  readonly datasourceTypes$: Observable<DatasourceTypeModel[]> = this.datasourceCTypeService.select(
    (st) => st.entityState || [],
  );

  private readonly datasourceTypeRemapping: Record<string, DatasourceTypeEnum> = {
    'google-ads': DatasourceTypeEnum.GoogleAdwords,
  };

  findModelByType(type: DatasourceTypeEnum) {
    const finalType = this.datasourceTypeRemapping[type] ?? type;
    return this.datasourceCTypeService.getData()?.find((d) => d.type === finalType);
  }

  /**
   * Extracts datasource model from schema URL
   *
   * @param schema
   * Could look like
   * - https://s.y42.com/integrations/google-adwords/0.0.5/source
   * - https://s.y42.com/integrations/file-upload/0.0.1/source#xlsx
   * - https://s.y42.com/frontend/0.0.1/modeling/sql-modeling-settings
   *
   * @returns
   * DatasourceTypeModel for the requested datasource or undefined if datasource type is not supported
   */
  findModelBySchema(
    schema: DatasourceSettings['$schema'],
    options?: { skipUnsupportedSchemaErrorLog: boolean },
  ): DatasourceTypeModel | undefined {
    const modelsMap = this.prepareModelsMap();
    const integrationTypeWithAliasRegex = /.+#(.+)/;
    const simpleIntegrationTypeRegExpMatch = integrationTypeWithAliasRegex.exec(schema);
    const modelKey = [...modelsMap.keys()].find((key) => simpleIntegrationTypeRegExpMatch?.includes(key));
    if (modelKey) {
      return modelsMap.get(modelKey)!;
    }

    const integrationTypeRegex = /.*\/integrations\/(.*)\/[0-9.]+\/(source|table)/;
    const modelingTypeRegex = /.*\/frontend\/[0-9.].[0-9.].[0-9.]\/modeling\/(ui|sql)-modeling-settings/;
    const integrationRegExpMatch = integrationTypeRegex.exec(schema);
    const modelingRegExpMatch = modelingTypeRegex.exec(schema);

    const modelTypeEnum: DatasourceTypeEnum | undefined =
      modelingRegExpMatch?.[1] === 'sql'
        ? DatasourceTypeEnum.SqlModel
        : modelingRegExpMatch?.[1] === 'ui'
        ? DatasourceTypeEnum.UiModel
        : undefined;

    if (modelTypeEnum && modelsMap.has(modelTypeEnum)) {
      return modelsMap.get(modelTypeEnum)!;
    }

    const integrationKey = [...modelsMap.keys()].find((key) => integrationRegExpMatch?.includes(key));
    const result = integrationKey ? modelsMap.get(integrationKey) : undefined;
    if (!result && !options?.skipUnsupportedSchemaErrorLog) {
      this.errorHandlerService.error('[DatasourceTypeQuery.findModelBySchema]: Unknown schema type.', {
        context: { schema, knownTypes: [...modelsMap.keys()] },
      });
    }
    return result;
  }

  private prepareModelsMap(): Map<DatasourceTypeEnum, DatasourceTypeModel> {
    const models: DatasourceTypeModel[] = this.datasourceCTypeService.getData() || [];
    const modelsMap = new Map<DatasourceTypeEnum, DatasourceTypeModel>(
      models.map((model) => {
        return [model.type, model];
      }),
    );

    Object.entries(this.datasourceTypeRemapping).forEach(([remapFrom, remapTo]) => {
      const model = modelsMap.get(remapTo);
      if (model) {
        modelsMap.set(remapFrom as DatasourceTypeEnum, model);
      } else {
        if (environment.production_build) {
          this.errorHandlerService.error(
            `[DatasourceTypeQuery.findModelBySchema]: Cannot remap datasource type. Target datasource type is not supported.`,
            {
              context: { from: remapFrom, to: remapTo },
            },
          );
        } else {
          throw new Error(
            `Can't remap datasource type from '${remapFrom}' to '${remapTo}'. '${remapTo}' is not supported.`,
          );
        }
      }
    });

    return modelsMap;
  }
}
